import { createContext, useContext, useEffect, useState } from "react";
import api, { errorMessage } from "../lib/api";

const AuthContext = createContext(null);

// v1.8.11 — Persistent login. JWT 1 tahun di localStorage. Sliding window refresh
// setiap app bootstrap biar user tidak kena logout selama device masih dipakai.
const TOKEN_KEY = "bvg_token";
const USER_KEY = "bvg_user";
const EXP_KEY = "bvg_token_expires_at";

// Decode JWT payload tanpa library (base64url → JSON). Return null kalau invalid.
const decodeJwt = (token) => {
    try {
        const payload = token.split(".")[1];
        const b64 = payload.replace(/-/g, "+").replace(/_/g, "/");
        return JSON.parse(atob(b64));
    } catch { return null; }
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null); // null = loading, false = anon, object = user
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const bootstrap = async () => {
            const token = localStorage.getItem(TOKEN_KEY);
            if (!token) {
                setUser(false);
                setLoading(false);
                return;
            }
            // Cek exp claim lokal dulu (hemat 1 round-trip kalau sudah expired)
            const payload = decodeJwt(token);
            if (payload?.exp && payload.exp * 1000 < Date.now()) {
                localStorage.removeItem(TOKEN_KEY);
                localStorage.removeItem(USER_KEY);
                localStorage.removeItem(EXP_KEY);
                setUser(false);
                setLoading(false);
                return;
            }
            try {
                const { data: me } = await api.get("/auth/me");
                setUser(me);
                localStorage.setItem(USER_KEY, JSON.stringify(me));
                // Sliding window: extend expiry setiap app load — token tidak akan expire
                // kalau user aktif pakai minimal 1x per 365 hari (praktis never).
                try {
                    const { data: ref } = await api.post("/auth/refresh");
                    if (ref?.access_token) {
                        localStorage.setItem(TOKEN_KEY, ref.access_token);
                        if (ref.expires_at) localStorage.setItem(EXP_KEY, ref.expires_at);
                    }
                } catch { /* refresh fail non-fatal — token lama masih valid */ }
            } catch {
                localStorage.removeItem(TOKEN_KEY);
                localStorage.removeItem(USER_KEY);
                localStorage.removeItem(EXP_KEY);
                setUser(false);
            } finally {
                setLoading(false);
            }
        };
        bootstrap();
    }, []);

    const login = async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            localStorage.setItem(TOKEN_KEY, data.access_token);
            localStorage.setItem(USER_KEY, JSON.stringify(data.user));
            if (data.expires_at) localStorage.setItem(EXP_KEY, data.expires_at);
            setUser(data.user);
            return { ok: true };
        } catch (err) {
            return { ok: false, message: errorMessage(err) };
        }
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch { /* ignore */ }
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        localStorage.removeItem(EXP_KEY);
        setUser(false);
    };

    // RBAC baru: semua authenticated user → akses penuh fitur bisnis.
    // Hanya is_owner → akses Account Center + endpoint admin berbahaya.
    const isOwner = () => Boolean(user && user.is_owner);
    const canWrite = () => Boolean(user);        // semua user bisa write business
    const canDelete = () => Boolean(user);       // semua user bisa delete business
    const isReadOnly = () => false;
    const isAdmin = () => Boolean(user && user.is_owner);  // legacy alias
    const hasRole = (...roles) => Boolean(user && roles.includes(user.role));

    return (
        <AuthContext.Provider
            value={{ user, loading, login, logout, isOwner, isAdmin, isReadOnly, canWrite, canDelete, hasRole }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
